import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _object_without_properties } from "@swc/helpers/_/_object_without_properties";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useState } from 'react';
import { compact } from 'lodash-es';
import { BottomSheetContentLayout } from '../BottomSheet';
import { useIsInBottomSheet } from '../BottomSheet/BottomSheet';
import { useDevice } from '../Device';
import { MenuItem } from '../MenuItem';
import { useOverlayContext } from '../OverlayContext';
import { Select } from '../Select';
import { TextField } from '../TextField';
import { MobileTextFieldTID } from '../TextField/BaseTextField/BaseTextField.utils';
import { MobileSummaryCard } from '../TextField/MobileSummaryCard';
import { Typography } from '../Typography';
import styles from './TextFieldWithDropdown.module.scss';
function DropdownSuffix(props) {
    const { value, onChange, options, width = 60, disabled, testId, size, onBlur } = props;
    return /*#__PURE__*/ _jsx(Select, {
        preventOutline: true,
        width: width,
        minWidth: width,
        size: size,
        disabled: disabled,
        SelectProps: {
            autoWidth: true
        },
        value: value,
        testId: testId,
        onChange: (e)=>onChange(e.target.value),
        onBlur: onBlur,
        children: options.map((param)=>{
            let { value: optionValue, label, disabled: optionDisabled, disabledReason } = param;
            return /*#__PURE__*/ _jsx(MenuItem, {
                value: optionValue,
                disabled: optionDisabled,
                tooltipProps: {
                    title: disabledReason !== null && disabledReason !== void 0 ? disabledReason : ''
                },
                testId: testId && "".concat(testId, "-option-").concat(optionValue),
                children: label
            }, optionValue);
        })
    });
}
function TextFieldWithDropdownInBottomSheet(props) {
    var _ref;
    const { value, onChange, options, dropdownWidth, validateInBottomSheet, deriveProps, testId, width: _width, minWidth: _minWidth } = props, rest = _object_without_properties(props, [
        "value",
        "onChange",
        "options",
        "dropdownWidth",
        "validateInBottomSheet",
        "deriveProps",
        "testId",
        "width",
        "minWidth"
    ]);
    const { hideBottomSheet } = useOverlayContext();
    const [tempText, setTempText] = useState(value.text);
    const [tempOption, setTempOption] = useState(value.option);
    const derived = (_ref = deriveProps === null || deriveProps === void 0 ? void 0 : deriveProps(tempOption)) !== null && _ref !== void 0 ? _ref : {};
    const validationResult = validateInBottomSheet === null || validateInBottomSheet === void 0 ? void 0 : validateInBottomSheet(tempText, tempOption);
    const error = validationResult ? validationResult.error : rest.error;
    const helperText = validationResult ? validationResult.helperText : rest.helperText;
    function handleSave() {
        var _rest_onBlur;
        (_rest_onBlur = rest.onBlur) === null || _rest_onBlur === void 0 ? void 0 : _rest_onBlur.call(rest, {});
        onChange({
            text: tempText,
            option: tempOption
        });
        hideBottomSheet();
    }
    return /*#__PURE__*/ _jsx(BottomSheetContentLayout, {
        contentClassName: styles.bottomSheetContent,
        footer: {
            primaryAction: {
                text: 'Save',
                onClick: handleSave
            }
        },
        children: /*#__PURE__*/ _jsx(TextField, _object_spread_props(_object_spread({}, rest, derived), {
            collapseHelperText: true,
            disableSuffixPadding: true,
            value: tempText,
            error: error,
            helperText: helperText,
            testId: testId && "".concat(testId, "-input"),
            onChange: (e)=>setTempText(e.target.value),
            onKeyDown: ()=>{},
            suffix: /*#__PURE__*/ _jsx(DropdownSuffix, {
                value: tempOption,
                onChange: setTempOption,
                options: options,
                width: dropdownWidth,
                size: rest.size,
                disabled: rest.disabled,
                testId: testId && "".concat(testId, "-dropdown")
            })
        }))
    });
}
function TextFieldWithDropdownMobileCard(props) {
    var _ref;
    const { value, testId, label, error, helperText, disabled, mobile } = props;
    const { pushBottomSheetPage } = useOverlayContext();
    return /*#__PURE__*/ _jsx(MobileSummaryCard, {
        testId: testId,
        items: [
            {
                title: label,
                titleTestId: MobileTextFieldTID.Label(testId),
                body: /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsx(Typography, {
                            ellipsis: true,
                            testId: MobileTextFieldTID.Value(testId),
                            skin: error ? 'error' : 'standard',
                            children: (_ref = mobile === null || mobile === void 0 ? void 0 : mobile.displayValue) !== null && _ref !== void 0 ? _ref : value.text
                        }),
                        helperText && /*#__PURE__*/ _jsx(Typography, {
                            testId: MobileTextFieldTID.HelperText(testId),
                            skin: error ? 'error' : 'secondary',
                            children: helperText
                        })
                    ]
                }),
                actions: compact([
                    !disabled && {
                        type: 'edit',
                        testId: MobileTextFieldTID.EditButton(testId),
                        onClick: ()=>{
                            pushBottomSheetPage({
                                content: /*#__PURE__*/ _jsx(TextFieldWithDropdownInBottomSheet, _object_spread({}, props))
                            });
                        }
                    }
                ])
            }
        ]
    });
}
export function TextFieldWithDropdown(props) {
    var _ref;
    const { value, onChange, options, dropdownWidth, deriveProps, validateInBottomSheet, testId, mobile } = props, rest = _object_without_properties(props, [
        "value",
        "onChange",
        "options",
        "dropdownWidth",
        "deriveProps",
        "validateInBottomSheet",
        "testId",
        "mobile"
    ]);
    const { isMobileDevice } = useDevice();
    const isInBottomSheet = useIsInBottomSheet();
    const shouldRenderAsMobileCard = (mobile === null || mobile === void 0 ? void 0 : mobile.renderAsCard) && isMobileDevice && !isInBottomSheet;
    if (shouldRenderAsMobileCard) {
        return /*#__PURE__*/ _jsx(TextFieldWithDropdownMobileCard, _object_spread({}, props));
    }
    const derived = (_ref = deriveProps === null || deriveProps === void 0 ? void 0 : deriveProps(value.option)) !== null && _ref !== void 0 ? _ref : {};
    return /*#__PURE__*/ _jsx(TextField, _object_spread_props(_object_spread({}, rest, derived), {
        disableSuffixPadding: true,
        value: value.text,
        validateInBottomSheet: validateInBottomSheet && ((text)=>validateInBottomSheet(text, value.option)),
        testId: testId && "".concat(testId, "-input"),
        onChange: (e)=>onChange({
                text: e.target.value,
                option: value.option
            }),
        mobile: mobile,
        suffix: /*#__PURE__*/ _jsx(DropdownSuffix, {
            value: value.option,
            onChange: (option)=>onChange({
                    text: value.text,
                    option
                }),
            options: options,
            width: dropdownWidth,
            size: rest.size,
            disabled: rest.disabled,
            testId: testId && "".concat(testId, "-dropdown"),
            onBlur: rest.onBlur
        })
    }));
}
